import type { FC } from 'react'

import { InfoBox } from '@skytech/ui'
import { useQuery } from '@tanstack/react-query'

import { Translation } from '@/app/translations/Translation'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import queries from '@/modules/ecom-policy/queries'
import ecomPolicyResources from '@/modules/ecom-policy/resources'
import { useShopNewOrderContext } from '@/modules/ecom-policy/shop/new-order/ShopNewOrder.context'

import Product from './Product'

import './section-products.scss'

const SectionProducts: FC<{
  cost: number
  isMaxAmountExceeded: boolean
  regressWizard?: () => void
  onActiveStepSet?: (newActiveStep: number) => void
}> = ({ cost, isMaxAmountExceeded, onActiveStepSet }) => {
  const shopCtx = useShopNewOrderContext()
  const [selectedPolicyId] = shopCtx.selectedPolicyId
  const [selectedProductIds] = shopCtx.selectedProductIds
  const [isWholeVendorsCatalogueVisible] = shopCtx.isWholeVendorsCatalogueVisible
  const [mandatoryProducts] = shopCtx.mandatoryProducts
  const [transportationProduct] = shopCtx.transportationProduct

  function getPriceListQuery() {
    if (!selectedPolicyId) return ['']
    if (isWholeVendorsCatalogueVisible)
      return queries.policyProductsFromWholeVendorCatalogue(selectedPolicyId)
    return queries.policyPriceList(selectedPolicyId)
  }

  const priceListQuery = useQuery(
    getPriceListQuery(),
    () => {
      if (!selectedPolicyId) throw Error('No policy selected!')
      if (isWholeVendorsCatalogueVisible)
        return ecomPolicyResources.getPolicyPriceListForWholeVendorCatalogue(selectedPolicyId)
      return ecomPolicyResources.getPolicyPriceList(selectedPolicyId)
    },
    { enabled: Boolean(selectedPolicyId) }
  )

  const products = priceListQuery.data?.response.filter(({ id }) =>
    selectedProductIds?.includes(id)
  )
  products?.push(...(mandatoryProducts || []))
  // For ecom orders made on policies with vendor_id = 7 (Dustin, Multiconsult), a transportation product needs to be added based on the zip/postal code address.
  if (transportationProduct) products?.push(transportationProduct)
  const productCurrency = products?.[0]?.currency

  return (
    <section>
      <h2>
        <Translation id="Products" />
      </h2>

      {isMaxAmountExceeded && (
        <InfoBox emphasis="error" marginTop={16} marginBottom={16} marginLeft={24} marginRight={24}>
          <Translation id="The maximum amount has been exceeded. Adjust the product selection to continue." />
        </InfoBox>
      )}

      <div className="step-confirm__products">
        {products?.map(product => {
          const { id, parentProductIdList, catId, isMandatory } = product

          return (
            <Product
              key={id}
              quantity={1}
              {...product}
              quantityLimit={product.quantityLimit}
              isParentProduct={!parentProductIdList}
              isMandatoryProduct={Boolean(isMandatory)}
              // The set of transportation products has category id 15 ("Frakt") and the range of postal codes as part of the product name.
              isTransportationProduct={catId === 15}
              onActiveStepSet={onActiveStepSet}
            />
          )
        })}
      </div>

      <div className="step-confirm__total">
        <div className="step-confirm__total__header">
          <Translation id="Total cost" />
        </div>

        <div className="step-confirm__total__cost">
          <FormattedNumberWithCurrency amount={cost} currency={productCurrency} />
        </div>
      </div>
    </section>
  )
}

export default SectionProducts
